import React, { useState, useEffect, useRef } from 'react';
import './AnalyticsView.css';

const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

function AnalyticsView() {
  const [cells, setCells] = useState([
    { id: 1, type: 'code', content: '', output: null, executing: false }
  ]);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [selectedTable, setSelectedTable] = useState('ads_ad');
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const hasFetched = useRef(false);

  // ── Notebook DB state (replaces localStorage notebooks) ──────────────────
  const [notebooks, setNotebooks] = useState([]);
  const [currentNotebookId, setCurrentNotebookId] = useState(null);
  const [nbLoading, setNbLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const autoSaveTimer = useRef(null);

  // ── UI state (unchanged from original) ───────────────────────────────────
  const [showNotebookDropdown, setShowNotebookDropdown] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newNotebookName, setNewNotebookName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [showQuickRef, setShowQuickRef] = useState(false);

  // ── Token helpers (multi-key fallback) ───────────────────────────────────
  const getToken = () =>
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('userToken') || '';

  const getAuthHeaders = () => {
    const token = getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Token ${token}` } : {}),
    };
  };

  // ============================================================
  // TABLE DISCOVERY (unchanged logic, expanded fallback)
  // ============================================================
  const fetchAvailableTables = async (retryCount = 0) => {
    const token = getToken();
    if (!token && retryCount < 10) {
      setTimeout(() => fetchAvailableTables(retryCount + 1), 400);
      return;
    }
    if (!token) {
      setConnectionStatus('error');
      setTablesLoading(false);
      loadFallbackTables();
      return;
    }
    try {
      setTablesLoading(true);
      setConnectionStatus('connecting');
      const response = await fetch(`${API_BASE}/notebook/tables/`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
      });
      if (response.status === 401) {
        if (retryCount < 2) { setTimeout(() => fetchAvailableTables(retryCount + 1), 800); return; }
        setConnectionStatus('error'); loadFallbackTables(); return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const list = data.tables || [];
      setTables(list);
      if (list.length > 0) {
        const hasAdsAd = list.find(t => t.value === 'ads_ad');
        setSelectedTable(hasAdsAd ? 'ads_ad' : list[0].value);
      }
      setConnectionStatus('connected');
    } catch (error) {
      setConnectionStatus('error');
      loadFallbackTables();
    } finally {
      setTablesLoading(false);
    }
  };

  const loadFallbackTables = () => {
    setTables([
      { value: 'ads_ad',            label: 'Ads (ads_ad)' },
      { value: 'ads_adimage',       label: 'Ad Images (ads_adimage)' },
      { value: 'ads_advideo',       label: 'Ad Videos (ads_advideo)' },
      { value: 'ads_category',      label: 'Categories (ads_category)' },
      { value: 'ads_job',           label: 'Jobs (ads_job)' },
      { value: 'ads_shop',          label: 'Shops (ads_shop)' },
      { value: 'ads_shopimage',     label: 'Shop Images (ads_shopimage)' },
      { value: 'ads_userprofile',   label: 'User Profiles (ads_userprofile)' },
      { value: 'ads_offer',         label: 'Offers (ads_offer)' },
      { value: 'ads_offermessage',  label: 'Offer Messages (ads_offermessage)' },
      { value: 'ads_notification',  label: 'Notifications (ads_notification)' },
      { value: 'ads_adlike',        label: 'Ad Likes (ads_adlike)' },
      { value: 'ads_userfollow',    label: 'User Follows (ads_userfollow)' },
      { value: 'ads_notebook',      label: 'Notebooks (ads_notebook)' },
      { value: 'auth_user',         label: 'Users (auth_user)' },
    ]);
  };

  // Boot: fetch tables + notebooks together
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const timer = setTimeout(() => {
      fetchAvailableTables();
      fetchNotebooks();
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // ============================================================
  // NOTEBOOK DB CRUD
  // ============================================================
  const fetchNotebooks = async () => {
    try {
      setNbLoading(true);
      const res = await fetch(`${API_BASE}/notebook/notebooks/`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.notebooks || [];
      setNotebooks(list);
      if (list.length > 0) {
        // Auto-open most recently updated notebook
        const latest = list[0];
        setCurrentNotebookId(latest.id);
        loadCellsFromNotebook(latest);
      }
    } catch (e) {
      console.error('Failed to fetch notebooks:', e);
    } finally {
      setNbLoading(false);
    }
  };

  const loadCellsFromNotebook = (nb) => {
    if (nb.cells && nb.cells.length > 0) {
      setCells(nb.cells.map(c => ({ ...c, executing: false })));
    } else {
      setCells([{ id: Date.now(), type: 'code', content: '', output: null, executing: false }]);
    }
  };

  const saveNotebook = async (nameOverride) => {
    const currentNb = notebooks.find(n => n.id === currentNotebookId);
    const name = nameOverride || currentNb?.name || 'Untitled Notebook';
    const cellsToSave = cells.map(c => ({
      id: c.id, type: c.type, content: c.content, output: c.output
    }));
    setSaveStatus('saving');
    try {
      const body = { name, cells: cellsToSave };
      if (currentNotebookId) body.id = currentNotebookId;
      const res = await fetch(`${API_BASE}/notebook/notebooks/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setCurrentNotebookId(data.id);
      setNotebooks(prev => {
        const exists = prev.find(n => n.id === data.id);
        if (exists) return prev.map(n => n.id === data.id ? { ...n, ...data } : n);
        return [data, ...prev];
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (e) {
      console.error('Save error:', e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Auto-save 2s after last cell edit
  useEffect(() => {
    if (!currentNotebookId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveNotebook(), 2000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [cells, currentNotebookId]);

  // Ctrl+S manual save
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveNotebook();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cells, currentNotebookId]);

  const createNewNotebook = async () => {
    const name = `Notebook ${notebooks.length + 1}`;
    setShowNotebookDropdown(false);
    try {
      const res = await fetch(`${API_BASE}/notebook/notebooks/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, cells: [] }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotebooks(prev => [data, ...prev]);
      setCurrentNotebookId(data.id);
      setCells([{ id: Date.now(), type: 'code', content: '', output: null, executing: false }]);
    } catch (e) {
      console.error('Create notebook failed:', e);
    }
  };

  const openNotebook = (nb) => {
    setCurrentNotebookId(nb.id);
    loadCellsFromNotebook(nb);
    setShowNotebookDropdown(false);
  };

  const renameNotebook = async () => {
    if (!newNotebookName.trim()) return;
    const id = renamingId || currentNotebookId;
    try {
      const res = await fetch(`${API_BASE}/notebook/notebooks/${id}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: newNotebookName.trim() }),
      });
      if (res.ok) {
        setNotebooks(prev =>
          prev.map(n => n.id === id ? { ...n, name: newNotebookName.trim() } : n)
        );
      }
    } catch (e) { console.error(e); }
    setShowRenameModal(false);
    setNewNotebookName('');
    setRenamingId(null);
  };

  const deleteNotebook = async (id) => {
    if (notebooks.length === 1) { alert('Cannot delete the last notebook'); return; }
    if (!window.confirm('Delete this notebook permanently from the database?')) return;
    try {
      await fetch(`${API_BASE}/notebook/notebooks/${id}/`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      const remaining = notebooks.filter(n => n.id !== id);
      setNotebooks(remaining);
      if (currentNotebookId === id && remaining.length > 0) {
        setCurrentNotebookId(remaining[0].id);
        loadCellsFromNotebook(remaining[0]);
      }
    } catch (e) { console.error(e); }
  };

  const formatLastSaved = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const currentNotebook = notebooks.find(n => n.id === currentNotebookId);
  const saveStatusLabel =
    saveStatus === 'saving' ? '⏳ Saving…'
    : saveStatus === 'saved'  ? '✓ Saved to DB'
    : saveStatus === 'error'  ? '⚠ Save failed'
    : currentNotebook        ? `Saved ${formatLastSaved(currentNotebook.updated_at)}`
    : nbLoading              ? 'Loading…'
    : 'Unsaved';

  // ============================================================
  // CELL OPERATIONS (all original logic preserved)
  // ============================================================
  const addCell = () => setCells([
    ...cells,
    { id: Date.now(), type: 'code', content: '', output: null, executing: false }
  ]);

  const updateCell = (id, content) =>
    setCells(cells.map(c => c.id === id ? { ...c, content } : c));

  const deleteCell = (id) => {
    if (cells.length > 1) setCells(cells.filter(c => c.id !== id));
  };

  // ✅ NEW: clear just this cell's output
  const clearCellOutput = (id) =>
    setCells(cells.map(c => c.id === id ? { ...c, output: null } : c));

  const moveCellUp = (id) => {
    const i = cells.findIndex(c => c.id === id);
    if (i > 0) { const n = [...cells]; [n[i-1], n[i]] = [n[i], n[i-1]]; setCells(n); }
  };
  const moveCellDown = (id) => {
    const i = cells.findIndex(c => c.id === id);
    if (i < cells.length - 1) { const n = [...cells]; [n[i], n[i+1]] = [n[i+1], n[i]]; setCells(n); }
  };

  const executeCell = async (id) => {
    const cell = cells.find(c => c.id === id);
    if (!cell || cell.executing || !cell.content.trim()) return;
    setCells(prev => prev.map(c => c.id === id ? { ...c, executing: true, output: null } : c));
    try {
      const code = cell.content.trim();
      const lower = code.toLowerCase();
      let result;
      if (lower === 'show tables' || lower === 'list tables') {
        result = { type: 'table', data: tables.map(t => ({ 'Table': t.value, 'Description': t.label })), rowCount: tables.length };
      } else if (lower.startsWith('describe') || lower.startsWith('show columns')) {
        const tableName = code.split(/\s+/)[1];
        result = await callDescribe(tableName);
      } else if (
        lower.startsWith('select') || lower.startsWith('with') ||
        lower.includes(' from ') || lower.startsWith('insert') ||
        lower.startsWith('update') || lower.startsWith('delete')
      ) {
        result = await callExecute(code);
      } else {
        result = { type: 'error', message: `Unknown command: "${code.split('\n')[0]}"\nTry: SELECT, SHOW TABLES, DESCRIBE <table>` };
      }
      setCells(prev => prev.map(c => c.id === id ? { ...c, executing: false, output: result } : c));
    } catch (err) {
      setCells(prev => prev.map(c => c.id === id ? { ...c, executing: false, output: { type: 'error', message: err.message } } : c));
    }
  };

  const callExecute = async (query) => {
    try {
      const res = await fetch(`${API_BASE}/notebook/execute/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ query, table: selectedTable }),
      });
      const data = await res.json();
      if (!res.ok) return { type: 'error', message: data.error || 'Query failed' };
      if (data.results !== undefined) {
        return { type: 'table', data: data.results, rowCount: data.count, executionTime: data.execution_time };
      }
      return { type: 'success', message: `✓ ${data.rows_affected ?? 0} row(s) affected (${data.execution_time}ms)` };
    } catch (err) {
      return { type: 'error', message: err.message };
    }
  };

  const callDescribe = async (tableName) => {
    try {
      const res = await fetch(`${API_BASE}/notebook/describe/${tableName}/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) return { type: 'error', message: data.error || 'Failed to describe table' };
      return { type: 'table', data: data.columns };
    } catch (err) {
      return { type: 'error', message: err.message };
    }
  };

  const runAllCells = () => cells.forEach(c => { if (c.type === 'code') executeCell(c.id); });
  const clearAllOutputs = () => setCells(cells.map(c => ({ ...c, output: null })));
  const handleKeyDown = (e, id) => {
    if (e.shiftKey && e.key === 'Enter') { e.preventDefault(); executeCell(id); }
  };

  // ============================================================
  // OUTPUT RENDERER (unchanged)
  // ============================================================
  const renderOutput = (output) => {
    if (!output) return null;
    switch (output.type) {
      case 'table':
        return (
          <div className="output-table-container">
            {output.rowCount !== undefined && (
              <div className="output-meta">
                {output.rowCount} rows{output.executionTime ? ` · ${output.executionTime}ms` : ''}
              </div>
            )}
            {output.data?.length > 0 ? (
              <div className="output-table-wrapper">
                <table className="output-table">
                  <thead>
                    <tr>{Object.keys(output.data[0]).map((k, i) => <th key={i}>{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {output.data.map((row, ri) => (
                      <tr key={ri}>
                        {Object.values(row).map((v, ci) => (
                          <td key={ci}>{v === null ? <span className="null-value">NULL</span> : String(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="empty-result">No rows returned</div>}
          </div>
        );
      case 'success':
        return <div className="output-success">✓ {output.message}</div>;
      case 'error':
        return <div className="output-error"><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>❌ {output.message}</pre></div>;
      case 'text':
        return <div className="output-text">{output.content}</div>;
      default:
        return <div className="output-text">{JSON.stringify(output)}</div>;
    }
  };

  const statusLabel = connectionStatus === 'connected' ? 'Connected'
    : connectionStatus === 'connecting' ? 'Connecting...'
    : 'Disconnected';

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="analytics-notebook">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="notebook-header">
        <div className="header-left">
          <div className="notebook-selector">
            <button
              className="current-notebook-btn"
              onClick={() => setShowNotebookDropdown(!showNotebookDropdown)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <div className="notebook-info">
                <span className="notebook-name">{currentNotebook?.name || 'New Notebook'}</span>
                <span className={`notebook-saved ${saveStatus === 'error' ? 'save-error' : saveStatus === 'saved' ? 'save-ok' : ''}`}>
                  {saveStatusLabel}
                </span>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>

            {/* Manual save button */}
            <button
              className="manual-save-btn"
              onClick={() => saveNotebook()}
              title="Save notebook (Ctrl+S)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              Save
            </button>

            {showNotebookDropdown && (
              <div className="notebook-dropdown">
                <div className="notebook-dropdown-header">
                  <span>Notebooks ({notebooks.length})</span>
                  <button onClick={createNewNotebook} className="create-notebook-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
                      <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    New
                  </button>
                </div>

                {nbLoading ? (
                  <div className="nb-loading-msg">Loading notebooks from database…</div>
                ) : notebooks.length === 0 ? (
                  <div className="nb-loading-msg">No saved notebooks yet. Click New to create one.</div>
                ) : (
                  notebooks.map(nb => (
                    <div key={nb.id} className={`notebook-option ${currentNotebookId === nb.id ? 'active' : ''}`}>
                      <div className="notebook-option-content" onClick={() => openNotebook(nb)}>
                        <span className="notebook-option-name">{nb.name}</span>
                        <span className="notebook-option-time">
                          {nb.cell_count || 0} cells · {formatLastSaved(nb.updated_at)}
                        </span>
                      </div>
                      <div className="notebook-option-actions">
                        <button
                          title="Rename"
                          onClick={e => {
                            e.stopPropagation();
                            setNewNotebookName(nb.name);
                            setRenamingId(nb.id);
                            setShowRenameModal(true);
                            setShowNotebookDropdown(false);
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                        {notebooks.length > 1 && (
                          <button
                            title="Delete"
                            onClick={e => { e.stopPropagation(); deleteNotebook(nb.id); }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="header-right">
          <button
            className={`toolbar-btn ${showQuickRef ? 'active' : ''}`}
            onClick={() => setShowQuickRef(!showQuickRef)}
            title="Quick Reference"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Toolbar (unchanged) ─────────────────────────────────────── */}
      <div className="notebook-toolbar">
        <div className="toolbar-left">
          <div className="connection-status">
            <div className={`status-indicator ${connectionStatus}`}></div>
            <span className="status-text">{statusLabel}</span>

            {connectionStatus === 'error' && (
              <button
                className="retry-btn"
                onClick={() => { hasFetched.current = false; fetchAvailableTables(0); }}
                title="Retry connection"
              >
                ↻ Retry
              </button>
            )}

            <div className="table-selector" onClick={() => setShowTableDropdown(!showTableDropdown)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>{tablesLoading ? 'Loading...' : (tables.find(t => t.value === selectedTable)?.label || 'Select Table')}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/>
              </svg>

              {showTableDropdown && (
                <div className="table-dropdown">
                  <div
                    className="table-option"
                    style={{ color: '#6366f1', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}
                    onClick={e => {
                      e.stopPropagation();
                      hasFetched.current = false;
                      fetchAvailableTables(0);
                      setShowTableDropdown(false);
                    }}
                  >
                    🔄 Refresh Tables ({tables.length} loaded)
                  </div>
                  {tables.map(t => (
                    <div
                      key={t.value}
                      className={`table-option ${selectedTable === t.value ? 'active' : ''}`}
                      onClick={e => { e.stopPropagation(); setSelectedTable(t.value); setShowTableDropdown(false); }}
                    >
                      {t.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="toolbar-divider"></div>
          <button className="toolbar-btn" onClick={addCell} title="Add Cell">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
          <button className="toolbar-btn" onClick={runAllCells} title="Run All">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <polygon points="5 3 19 12 5 21 5 3" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
          <button className="toolbar-btn" onClick={clearAllOutputs} title="Clear All Outputs">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
        </div>
        <div className="toolbar-right">
          <button className="toolbar-btn-primary" onClick={addCell}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Code
          </button>
        </div>
      </div>

      {/* ── Cells ───────────────────────────────────────────────────── */}
      <div className="cells-container">
        {cells.map((cell, index) => (
          <div key={cell.id} className={`notebook-cell ${cell.executing ? 'executing' : ''}`}>
            <div className="cell-sidebar">
              <div className="cell-number">[{index + 1}]:</div>
            </div>
            <div className="cell-content">
              <div className="cell-input">
                <textarea
                  className="cell-textarea"
                  value={cell.content}
                  onChange={e => updateCell(cell.id, e.target.value)}
                  onKeyDown={e => handleKeyDown(e, cell.id)}
                  placeholder={`# Enter SQL query\nSELECT * FROM ${selectedTable} LIMIT 10;\n\n# Or try:\nSHOW TABLES\nDESCRIBE ${selectedTable}`}
                  spellCheck={false}
                />
                <div className="cell-actions">
                  <button
                    className="cell-action-btn execute-btn"
                    onClick={() => executeCell(cell.id)}
                    disabled={cell.executing}
                    title="Run (Shift+Enter)"
                  >
                    {cell.executing
                      ? <div className="spinner-small"></div>
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/></svg>
                    }
                  </button>
                  <button className="cell-action-btn" onClick={() => moveCellUp(cell.id)} title="Move Up">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="18 15 12 9 6 15" stroke="currentColor" strokeWidth="2"/></svg>
                  </button>
                  <button className="cell-action-btn" onClick={() => moveCellDown(cell.id)} title="Move Down">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/></svg>
                  </button>
                  <button className="cell-action-btn delete-btn" onClick={() => deleteCell(cell.id)} title="Delete Cell">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* ✅ OUTPUT with per-cell clear button */}
              {cell.output && (
                <div className="cell-output">
                  <div className="output-toolbar">
                    <span className="output-label">
                      {cell.output.type === 'error' ? '❌ Error' : cell.output.type === 'success' ? '✓ Success' : '📊 Output'}
                    </span>
                    <button
                      className="clear-output-btn"
                      onClick={() => clearCellOutput(cell.id)}
                      title="Clear this output"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                      Clear output
                    </button>
                  </div>
                  {renderOutput(cell.output)}
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="add-cell-section">
          <button className="add-cell-btn" onClick={addCell}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Add Cell
          </button>
        </div>
      </div>

      {/* ── Quick Reference (unchanged + Ctrl+S shortcut added) ──────── */}
      {showQuickRef && (
        <div className="quick-reference">
          <div className="quick-ref-header">
            <h3>Quick Reference</h3>
            <button onClick={() => setShowQuickRef(false)} className="close-quick-ref">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>
          <div className="reference-section">
            <h4>Available Tables ({tables.length})</h4>
            {tables.map(t => <code key={t.value} style={{ display: 'block', marginBottom: 4 }}>{t.value}</code>)}
          </div>
          <div className="reference-section">
            <h4>Examples</h4>
            <code>SELECT * FROM ads_ad LIMIT 10;</code>
            <code>SELECT * FROM ads_shop LIMIT 10;</code>
            <code>SELECT * FROM ads_job LIMIT 10;</code>
            <code>SELECT * FROM ads_offer LIMIT 10;</code>
            <code>SELECT * FROM ads_offermessage LIMIT 10;</code>
            <code>SELECT * FROM ads_notification LIMIT 10;</code>
            <code>SELECT * FROM ads_adlike LIMIT 10;</code>
            <code>SELECT * FROM auth_user LIMIT 10;</code>
            <code>SHOW TABLES</code>
            <code>DESCRIBE ads_ad</code>
          </div>
          <div className="reference-section">
            <h4>Shortcuts</h4>
            <div className="shortcut"><kbd>Shift</kbd> + <kbd>Enter</kbd> — Run Cell</div>
            <div className="shortcut"><kbd>Ctrl</kbd> + <kbd>S</kbd> — Save Notebook</div>
          </div>
        </div>
      )}

      {/* ── Rename Modal (unchanged) ─────────────────────────────────── */}
      {showRenameModal && (
        <div className="modal-overlay" onClick={() => setShowRenameModal(false)}>
          <div className="modal-box-small" onClick={e => e.stopPropagation()}>
            <h3>Rename Notebook</h3>
            <input
              type="text"
              className="rename-input"
              value={newNotebookName}
              onChange={e => setNewNotebookName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && renameNotebook()}
              autoFocus
              placeholder="Enter notebook name"
            />
            <div className="modal-actions-small">
              <button className="btn-cancel-small" onClick={() => setShowRenameModal(false)}>Cancel</button>
              <button className="btn-submit-small" onClick={renameNotebook}>Rename</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsView;






















// import React, { useState, useEffect, useRef } from 'react';
// import './AnalyticsView.css';

// function AnalyticsView() {
//   const [cells, setCells] = useState([
//     { id: 1, type: 'code', content: '', output: null, executing: false }
//   ]);
//   const [connectionStatus, setConnectionStatus] = useState('connecting');
//   const [selectedTable, setSelectedTable] = useState('ads_ad');
//   const [showTableDropdown, setShowTableDropdown] = useState(false);
//   const [tables, setTables] = useState([]);
//   const [tablesLoading, setTablesLoading] = useState(true);
//   const hasFetched = useRef(false);

//   const [notebooks, setNotebooks] = useState(() => {
//     try {
//       const saved = localStorage.getItem('analyticsNotebooks');
//       if (saved) {
//         const parsed = JSON.parse(saved);
//         return parsed.map(nb => ({ ...nb, lastSaved: new Date(nb.lastSaved) }));
//       }
//     } catch (e) { console.error(e); }
//     return [{ id: 1, name: 'Untitled Notebook', lastSaved: new Date(), cells: [] }];
//   });

//   const [currentNotebookId, setCurrentNotebookId] = useState(() => {
//     try { return parseInt(localStorage.getItem('currentNotebookId') || '1'); }
//     catch { return 1; }
//   });

//   const [showNotebookDropdown, setShowNotebookDropdown] = useState(false);
//   const [showRenameModal, setShowRenameModal] = useState(false);
//   const [newNotebookName, setNewNotebookName] = useState('');
//   const [showQuickRef, setShowQuickRef] = useState(false);

//   // ── FIX: Read token with multiple fallback keys ──────────────────────────
//   const getToken = () =>
//     localStorage.getItem('authToken') ||
//     localStorage.getItem('token') ||
//     localStorage.getItem('auth_token') ||
//     localStorage.getItem('userToken') ||
//     '';

//   const getAuthHeaders = () => {
//     const token = getToken();
//     return {
//       'Content-Type': 'application/json',
//       ...(token ? { 'Authorization': `Token ${token}` } : {}),
//     };
//   };

//   // ── FIX: Robust fetch with retry + better error messages ─────────────────
//   const fetchAvailableTables = async (retryCount = 0) => {
//     const token = getToken();

//     if (!token && retryCount < 10) {
//       console.log(`⏳ No token yet, retrying in 400ms... (attempt ${retryCount + 1})`);
//       setTimeout(() => fetchAvailableTables(retryCount + 1), 400);
//       return;
//     }

//     if (!token) {
//       console.warn('❌ No auth token found after retries');
//       setConnectionStatus('error');
//       setTablesLoading(false);
//       loadFallbackTables();
//       return;
//     }

//     try {
//       setTablesLoading(true);
//       setConnectionStatus('connecting');

//       console.log('🔌 Fetching tables with token:', token.substring(0, 8) + '...');

//       const response = await fetch('https://imselling-marketplace-production.up.railway.app/api/notebook/tables/', {
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Token ${token}`,
//         },
//       });

//       if (response.status === 401) {
//         console.error('❌ 401 — token rejected. Retrying once...');
//         if (retryCount < 2) {
//           setTimeout(() => fetchAvailableTables(retryCount + 1), 800);
//           return;
//         }
//         setConnectionStatus('error');
//         loadFallbackTables();
//         return;
//       }

//       if (!response.ok) throw new Error(`HTTP ${response.status}`);

//       const data = await response.json();
//       console.log('✅ Tables loaded:', data.tables?.length);

//       const list = data.tables || [];
//       setTables(list);
//       if (list.length > 0) {
//         const hasAdsAd = list.find(t => t.value === 'ads_ad');
//         setSelectedTable(hasAdsAd ? 'ads_ad' : list[0].value);
//       }
//       setConnectionStatus('connected');

//     } catch (error) {
//       console.error('❌ Failed to fetch tables:', error);
//       setConnectionStatus('error');
//       loadFallbackTables();
//     } finally {
//       setTablesLoading(false);
//     }
//   };

//   // ── FIX: Expanded fallback table list including new models ───────────────
//   const loadFallbackTables = () => {
//     setTables([
//       { value: 'ads_ad',            label: 'Ads (ads_ad)' },
//       { value: 'ads_adimage',       label: 'Ad Images (ads_adimage)' },
//       { value: 'ads_advideo',       label: 'Ad Videos (ads_advideo)' },
//       { value: 'ads_category',      label: 'Categories (ads_category)' },
//       { value: 'ads_job',           label: 'Jobs (ads_job)' },
//       { value: 'ads_shop',          label: 'Shops (ads_shop)' },
//       { value: 'ads_shopimage',     label: 'Shop Images (ads_shopimage)' },
//       { value: 'ads_userprofile',   label: 'User Profiles (ads_userprofile)' },
//       { value: 'ads_offer',         label: 'Offers (ads_offer)' },
//       { value: 'ads_offermessage',  label: 'Offer Messages (ads_offermessage)' },
//       { value: 'ads_notification',  label: 'Notifications (ads_notification)' },
//       { value: 'ads_adlike',        label: 'Ad Likes (ads_adlike)' },
//       { value: 'ads_userfollow',    label: 'User Follows (ads_userfollow)' },
//       { value: 'auth_user',         label: 'Users (auth_user)' },
//     ]);
//   };

//   // ── FIX: Delay initial fetch to let AuthContext restore token ────────────
//   useEffect(() => {
//     if (hasFetched.current) return;
//     hasFetched.current = true;
//     const timer = setTimeout(() => fetchAvailableTables(), 800);
//     return () => clearTimeout(timer);
//   }, []);

//   const currentNotebook = notebooks.find(n => n.id === currentNotebookId);

//   useEffect(() => {
//     if (currentNotebook?.cells?.length > 0) {
//       setCells(currentNotebook.cells);
//     } else {
//       setCells([{ id: Date.now(), type: 'code', content: '', output: null, executing: false }]);
//     }
//   }, [currentNotebookId]);

//   useEffect(() => {
//     const t = setTimeout(() => {
//       setNotebooks(prev => prev.map(nb =>
//         nb.id === currentNotebookId ? { ...nb, cells, lastSaved: new Date() } : nb
//       ));
//     }, 1000);
//     return () => clearTimeout(t);
//   }, [cells, currentNotebookId]);

//   useEffect(() => {
//     try { localStorage.setItem('analyticsNotebooks', JSON.stringify(notebooks)); }
//     catch (e) { console.error(e); }
//   }, [notebooks]);

//   useEffect(() => {
//     try { localStorage.setItem('currentNotebookId', currentNotebookId.toString()); }
//     catch (e) { console.error(e); }
//   }, [currentNotebookId]);

//   const createNotebook = () => {
//     const nb = { id: Date.now(), name: `Notebook ${notebooks.length + 1}`, lastSaved: new Date(), cells: [] };
//     setNotebooks([...notebooks, nb]);
//     setCurrentNotebookId(nb.id);
//     setShowNotebookDropdown(false);
//   };

//   const renameNotebook = () => {
//     if (newNotebookName.trim()) {
//       setNotebooks(prev => prev.map(nb =>
//         nb.id === currentNotebookId ? { ...nb, name: newNotebookName.trim() } : nb
//       ));
//       setShowRenameModal(false);
//       setNewNotebookName('');
//     }
//   };

//   const deleteNotebook = (id) => {
//     if (notebooks.length === 1) { alert('Cannot delete the last notebook'); return; }
//     if (window.confirm('Delete this notebook?')) {
//       setNotebooks(prev => prev.filter(nb => nb.id !== id));
//       if (currentNotebookId === id) setCurrentNotebookId(notebooks.find(nb => nb.id !== id).id);
//     }
//   };

//   const formatLastSaved = (date) => {
//     if (!date) return '';
//     const diff = Math.floor((Date.now() - new Date(date)) / 1000);
//     if (diff < 60) return 'Just now';
//     if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
//     if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
//     return new Date(date).toLocaleDateString();
//   };

//   const addCell = () => setCells([...cells, { id: Date.now(), type: 'code', content: '', output: null, executing: false }]);
//   const updateCell = (id, content) => setCells(cells.map(c => c.id === id ? { ...c, content } : c));
//   const deleteCell = (id) => { if (cells.length > 1) setCells(cells.filter(c => c.id !== id)); };
//   const moveCellUp = (id) => {
//     const i = cells.findIndex(c => c.id === id);
//     if (i > 0) { const n = [...cells]; [n[i-1], n[i]] = [n[i], n[i-1]]; setCells(n); }
//   };
//   const moveCellDown = (id) => {
//     const i = cells.findIndex(c => c.id === id);
//     if (i < cells.length - 1) { const n = [...cells]; [n[i], n[i+1]] = [n[i+1], n[i]]; setCells(n); }
//   };

//   const executeCell = async (id) => {
//     const cell = cells.find(c => c.id === id);
//     if (!cell || cell.executing || !cell.content.trim()) return;

//     setCells(prev => prev.map(c => c.id === id ? { ...c, executing: true, output: null } : c));

//     try {
//       const code = cell.content.trim();
//       const lower = code.toLowerCase();
//       let result;

//       if (lower === 'show tables' || lower === 'list tables') {
//         result = { type: 'table', data: tables.map(t => ({ 'Table': t.value, 'Description': t.label })), rowCount: tables.length };
//       } else if (lower.startsWith('describe') || lower.startsWith('show columns')) {
//         const tableName = code.split(/\s+/)[1];
//         result = await callDescribe(tableName);
//       } else if (
//         lower.startsWith('select') || lower.startsWith('with') ||
//         lower.includes(' from ') || lower.startsWith('insert') ||
//         lower.startsWith('update') || lower.startsWith('delete')
//       ) {
//         result = await callExecute(code);
//       } else {
//         result = { type: 'error', message: `Unknown command: "${code.split('\n')[0]}"\nTry: SELECT, SHOW TABLES, DESCRIBE <table>` };
//       }

//       setCells(prev => prev.map(c => c.id === id ? { ...c, executing: false, output: result } : c));
//     } catch (err) {
//       setCells(prev => prev.map(c => c.id === id ? { ...c, executing: false, output: { type: 'error', message: err.message } } : c));
//     }
//   };

//   const callExecute = async (query) => {
//     try {
//       const res = await fetch('https://imselling-marketplace-production.up.railway.app/api/notebook/execute/', {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: JSON.stringify({ query, table: selectedTable }),
//       });
//       const data = await res.json();
//       if (!res.ok) return { type: 'error', message: data.error || 'Query failed' };
//       if (data.results !== undefined) {
//         return { type: 'table', data: data.results, rowCount: data.count, executionTime: data.execution_time };
//       }
//       return { type: 'success', message: `✓ ${data.rows_affected ?? 0} row(s) affected (${data.execution_time}ms)` };
//     } catch (err) {
//       return { type: 'error', message: err.message };
//     }
//   };

//   const callDescribe = async (tableName) => {
//     try {
//       const res = await fetch(`https://imselling-marketplace-production.up.railway.app/api/notebook/describe/${tableName}/`, {
//         headers: getAuthHeaders(),
//       });
//       const data = await res.json();
//       if (!res.ok) return { type: 'error', message: data.error || 'Failed to describe table' };
//       return { type: 'table', data: data.columns };
//     } catch (err) {
//       return { type: 'error', message: err.message };
//     }
//   };

//   const runAllCells = () => cells.forEach(c => { if (c.type === 'code') executeCell(c.id); });
//   const clearAllOutputs = () => setCells(cells.map(c => ({ ...c, output: null })));
//   const handleKeyDown = (e, id) => { if (e.shiftKey && e.key === 'Enter') { e.preventDefault(); executeCell(id); } };

//   const renderOutput = (output) => {
//     if (!output) return null;
//     switch (output.type) {
//       case 'table':
//         return (
//           <div className="output-table-container">
//             {output.rowCount !== undefined && (
//               <div className="output-meta">
//                 {output.rowCount} rows{output.executionTime ? ` · ${output.executionTime}ms` : ''}
//               </div>
//             )}
//             {output.data?.length > 0 ? (
//               <div className="output-table-wrapper">
//                 <table className="output-table">
//                   <thead>
//                     <tr>{Object.keys(output.data[0]).map((k, i) => <th key={i}>{k}</th>)}</tr>
//                   </thead>
//                   <tbody>
//                     {output.data.map((row, ri) => (
//                       <tr key={ri}>
//                         {Object.values(row).map((v, ci) => (
//                           <td key={ci}>{v === null ? <span className="null-value">NULL</span> : String(v)}</td>
//                         ))}
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             ) : <div className="empty-result">No rows returned</div>}
//           </div>
//         );
//       case 'success':
//         return <div className="output-success">✓ {output.message}</div>;
//       case 'error':
//         return <div className="output-error"><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>❌ {output.message}</pre></div>;
//       case 'text':
//         return <div className="output-text">{output.content}</div>;
//       default:
//         return <div className="output-text">{JSON.stringify(output)}</div>;
//     }
//   };

//   const statusLabel = connectionStatus === 'connected' ? 'Connected'
//     : connectionStatus === 'connecting' ? 'Connecting...'
//     : 'Disconnected';

//   return (
//     <div className="analytics-notebook">
//       {/* ── Header ─────────────────────────────────────────────────── */}
//       <div className="notebook-header">
//         <div className="header-left">
//           <div className="notebook-selector">
//             <button className="current-notebook-btn" onClick={() => setShowNotebookDropdown(!showNotebookDropdown)}>
//               <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
//                 <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
//                 <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
//               </svg>
//               <div className="notebook-info">
//                 <span className="notebook-name">{currentNotebook?.name}</span>
//                 <span className="notebook-saved">Saved {formatLastSaved(currentNotebook?.lastSaved)}</span>
//               </div>
//               <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
//                 <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/>
//               </svg>
//             </button>

//             {showNotebookDropdown && (
//               <div className="notebook-dropdown">
//                 <div className="notebook-dropdown-header">
//                   <span>Notebooks</span>
//                   <button onClick={createNotebook} className="create-notebook-btn">
//                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
//                       <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
//                       <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
//                     </svg>
//                     New
//                   </button>
//                 </div>
//                 {notebooks.map(nb => (
//                   <div key={nb.id} className={`notebook-option ${currentNotebookId === nb.id ? 'active' : ''}`}>
//                     <div className="notebook-option-content" onClick={() => { setCurrentNotebookId(nb.id); setShowNotebookDropdown(false); }}>
//                       <span className="notebook-option-name">{nb.name}</span>
//                       <span className="notebook-option-time">{formatLastSaved(nb.lastSaved)}</span>
//                     </div>
//                     <div className="notebook-option-actions">
//                       <button onClick={e => {
//                         e.stopPropagation();
//                         setNewNotebookName(nb.name);
//                         setCurrentNotebookId(nb.id);
//                         setShowRenameModal(true);
//                         setShowNotebookDropdown(false);
//                       }}>
//                         <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                           <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
//                           <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
//                         </svg>
//                       </button>
//                       {notebooks.length > 1 && (
//                         <button onClick={e => { e.stopPropagation(); deleteNotebook(nb.id); }}>
//                           <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                             <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
//                             <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
//                           </svg>
//                         </button>
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="header-right">
//           <button
//             className={`toolbar-btn ${showQuickRef ? 'active' : ''}`}
//             onClick={() => setShowQuickRef(!showQuickRef)}
//             title="Quick Reference"
//           >
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//               <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
//               <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2"/>
//               <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2"/>
//             </svg>
//           </button>
//         </div>
//       </div>

//       {/* ── Toolbar ────────────────────────────────────────────────── */}
//       <div className="notebook-toolbar">
//         <div className="toolbar-left">
//           <div className="connection-status">
//             <div className={`status-indicator ${connectionStatus}`}></div>
//             <span className="status-text">{statusLabel}</span>

//             {/* ── FIX: Retry button when disconnected ── */}
//             {connectionStatus === 'error' && (
//               <button
//                 className="retry-btn"
//                 onClick={() => { hasFetched.current = false; fetchAvailableTables(0); }}
//                 title="Retry connection"
//               >
//                 ↻ Retry
//               </button>
//             )}

//             <div className="table-selector" onClick={() => setShowTableDropdown(!showTableDropdown)}>
//               <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
//                 <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
//                 <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
//                 <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
//                 <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
//               </svg>
//               <span>{tablesLoading ? 'Loading...' : (tables.find(t => t.value === selectedTable)?.label || 'Select Table')}</span>
//               <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                 <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/>
//               </svg>

//               {showTableDropdown && (
//                 <div className="table-dropdown">
//                   <div
//                     className="table-option"
//                     style={{ color: '#6366f1', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}
//                     onClick={e => {
//                       e.stopPropagation();
//                       hasFetched.current = false;
//                       fetchAvailableTables(0);
//                       setShowTableDropdown(false);
//                     }}
//                   >
//                     🔄 Refresh Tables ({tables.length} loaded)
//                   </div>
//                   {tables.map(t => (
//                     <div
//                       key={t.value}
//                       className={`table-option ${selectedTable === t.value ? 'active' : ''}`}
//                       onClick={e => { e.stopPropagation(); setSelectedTable(t.value); setShowTableDropdown(false); }}
//                     >
//                       {t.label}
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           <div className="toolbar-divider"></div>
//           <button className="toolbar-btn" onClick={addCell} title="Add Cell">
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//               <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
//               <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
//             </svg>
//           </button>
//           <button className="toolbar-btn" onClick={runAllCells} title="Run All">
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//               <polygon points="5 3 19 12 5 21 5 3" stroke="currentColor" strokeWidth="2"/>
//             </svg>
//           </button>
//           <button className="toolbar-btn" onClick={clearAllOutputs} title="Clear Outputs">
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//               <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
//               <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" strokeWidth="2"/>
//             </svg>
//           </button>
//         </div>
//         <div className="toolbar-right">
//           <button className="toolbar-btn-primary" onClick={addCell}>
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//               <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
//               <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
//             </svg>
//             Code
//           </button>
//         </div>
//       </div>

//       {/* ── Cells ──────────────────────────────────────────────────── */}
//       <div className="cells-container">
//         {cells.map((cell, index) => (
//           <div key={cell.id} className={`notebook-cell ${cell.executing ? 'executing' : ''}`}>
//             <div className="cell-sidebar">
//               <div className="cell-number">[{index + 1}]:</div>
//             </div>
//             <div className="cell-content">
//               <div className="cell-input">
//                 <textarea
//                   className="cell-textarea"
//                   value={cell.content}
//                   onChange={e => updateCell(cell.id, e.target.value)}
//                   onKeyDown={e => handleKeyDown(e, cell.id)}
//                   placeholder={`# Enter SQL query\nSELECT * FROM ${selectedTable} LIMIT 10;\n\n# Or try:\nSHOW TABLES\nDESCRIBE ${selectedTable}`}
//                   spellCheck={false}
//                 />
//                 <div className="cell-actions">
//                   <button className="cell-action-btn execute-btn" onClick={() => executeCell(cell.id)} disabled={cell.executing} title="Run (Shift+Enter)">
//                     {cell.executing
//                       ? <div className="spinner-small"></div>
//                       : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/></svg>
//                     }
//                   </button>
//                   <button className="cell-action-btn" onClick={() => moveCellUp(cell.id)} title="Move Up">
//                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="18 15 12 9 6 15" stroke="currentColor" strokeWidth="2"/></svg>
//                   </button>
//                   <button className="cell-action-btn" onClick={() => moveCellDown(cell.id)} title="Move Down">
//                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/></svg>
//                   </button>
//                   <button className="cell-action-btn delete-btn" onClick={() => deleteCell(cell.id)} title="Delete">
//                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
//                       <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
//                       <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
//                     </svg>
//                   </button>
//                 </div>
//               </div>
//               {cell.output && <div className="cell-output">{renderOutput(cell.output)}</div>}
//             </div>
//           </div>
//         ))}
//         <div className="add-cell-section">
//           <button className="add-cell-btn" onClick={addCell}>
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//               <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
//               <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
//             </svg>
//             Add Cell
//           </button>
//         </div>
//       </div>

//       {/* ── Quick Reference ─────────────────────────────────────────── */}
//       {showQuickRef && (
//         <div className="quick-reference">
//           <div className="quick-ref-header">
//             <h3>Quick Reference</h3>
//             <button onClick={() => setShowQuickRef(false)} className="close-quick-ref">
//               <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//                 <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
//                 <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
//               </svg>
//             </button>
//           </div>
//           <div className="reference-section">
//             <h4>Available Tables ({tables.length})</h4>
//             {tables.map(t => <code key={t.value} style={{ display: 'block', marginBottom: 4 }}>{t.value}</code>)}
//           </div>
//           <div className="reference-section">
//             <h4>Examples</h4>
//             <code>SELECT * FROM ads_ad LIMIT 10;</code>
//             <code>SELECT * FROM ads_shop LIMIT 10;</code>
//             <code>SELECT * FROM ads_job LIMIT 10;</code>
//             <code>SELECT * FROM ads_offer LIMIT 10;</code>
//             <code>SELECT * FROM ads_offermessage LIMIT 10;</code>
//             <code>SELECT * FROM ads_notification LIMIT 10;</code>
//             <code>SELECT * FROM ads_adlike LIMIT 10;</code>
//             <code>SELECT * FROM auth_user LIMIT 10;</code>
//             <code>SHOW TABLES</code>
//             <code>DESCRIBE ads_ad</code>
//           </div>
//           <div className="reference-section">
//             <h4>Shortcuts</h4>
//             <div className="shortcut"><kbd>Shift</kbd> + <kbd>Enter</kbd> — Run Cell</div>
//           </div>
//         </div>
//       )}

//       {/* ── Rename Modal ────────────────────────────────────────────── */}
//       {showRenameModal && (
//         <div className="modal-overlay" onClick={() => setShowRenameModal(false)}>
//           <div className="modal-box-small" onClick={e => e.stopPropagation()}>
//             <h3>Rename Notebook</h3>
//             <input
//               type="text"
//               className="rename-input"
//               value={newNotebookName}
//               onChange={e => setNewNotebookName(e.target.value)}
//               onKeyDown={e => e.key === 'Enter' && renameNotebook()}
//               autoFocus
//               placeholder="Enter notebook name"
//             />
//             <div className="modal-actions-small">
//               <button className="btn-cancel-small" onClick={() => setShowRenameModal(false)}>Cancel</button>
//               <button className="btn-submit-small" onClick={renameNotebook}>Rename</button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default AnalyticsView;

























// // import React, { useState, useEffect, useRef } from 'react';
// // import './AnalyticsView.css';

// // function AnalyticsView() {
// //   const [cells, setCells] = useState([
// //     { id: 1, type: 'code', content: '', output: null, executing: false }
// //   ]);
// //   const [connectionStatus, setConnectionStatus] = useState('connecting');
// //   const [selectedTable, setSelectedTable] = useState('ads_ad');
// //   const [showTableDropdown, setShowTableDropdown] = useState(false);
// //   const [tables, setTables] = useState([]);
// //   const [tablesLoading, setTablesLoading] = useState(true);
// //   const hasFetched = useRef(false);  // ✅ prevent double-fetch in StrictMode

// //   const [notebooks, setNotebooks] = useState(() => {
// //     try {
// //       const saved = localStorage.getItem('analyticsNotebooks');
// //       if (saved) {
// //         const parsed = JSON.parse(saved);
// //         return parsed.map(nb => ({ ...nb, lastSaved: new Date(nb.lastSaved) }));
// //       }
// //     } catch (e) { console.error(e); }
// //     return [{ id: 1, name: 'Untitled Notebook', lastSaved: new Date(), cells: [] }];
// //   });

// //   const [currentNotebookId, setCurrentNotebookId] = useState(() => {
// //     try { return parseInt(localStorage.getItem('currentNotebookId') || '1'); }
// //     catch { return 1; }
// //   });

// //   const [showNotebookDropdown, setShowNotebookDropdown] = useState(false);
// //   const [showRenameModal, setShowRenameModal] = useState(false);
// //   const [newNotebookName, setNewNotebookName] = useState('');
// //   const [showQuickRef, setShowQuickRef] = useState(false);

// //   // ✅ KEY FIX: Read token fresh at call time, with retry if not yet available
// //   const getToken = () => localStorage.getItem('authToken');

// //   const getAuthHeaders = () => {
// //     const token = getToken();
// //     return {
// //       'Content-Type': 'application/json',
// //       ...(token ? { 'Authorization': `Token ${token}` } : {}),
// //     };
// //   };

// //   // ✅ FIXED: Fetch with retry — waits up to 3s for token to appear in localStorage
// //   const fetchAvailableTables = async (retryCount = 0) => {
// //     const token = getToken();

// //     // If no token yet and we haven't retried too many times, wait and retry
// //     if (!token && retryCount < 6) {
// //       console.log(`⏳ No token yet, retrying in 500ms... (attempt ${retryCount + 1})`);
// //       setTimeout(() => fetchAvailableTables(retryCount + 1), 500);
// //       return;
// //     }

// //     if (!token) {
// //       console.warn('❌ No auth token found after retries — cannot fetch tables');
// //       setConnectionStatus('error');
// //       setTablesLoading(false);
// //       // Still load fallback static list so UI is usable
// //       loadFallbackTables();
// //       return;
// //     }

// //     try {
// //       setTablesLoading(true);
// //       setConnectionStatus('connecting');

// //       console.log('🔌 Fetching tables with token:', token.substring(0, 8) + '...');

// //       const response = await fetch('https://imselling-marketplace-production.up.railway.app/api/notebook/tables/', {
// //         headers: {
// //           'Content-Type': 'application/json',
// //           'Authorization': `Token ${token}`,
// //         },
// //       });

// //       if (response.status === 401) {
// //         console.error('❌ 401 on notebook/tables — token rejected by server');
// //         setConnectionStatus('error');
// //         loadFallbackTables();
// //         return;
// //       }

// //       if (!response.ok) {
// //         throw new Error(`HTTP ${response.status}`);
// //       }

// //       const data = await response.json();
// //       console.log('✅ Tables loaded:', data.tables?.length, 'tables');

// //       setTables(data.tables || []);
// //       if (data.tables?.length > 0) {
// //         const hasAdsAd = data.tables.find(t => t.value === 'ads_ad');
// //         setSelectedTable(hasAdsAd ? 'ads_ad' : data.tables[0].value);
// //       }
// //       setConnectionStatus('connected');

// //     } catch (error) {
// //       console.error('❌ Failed to fetch tables:', error);
// //       setConnectionStatus('error');
// //       loadFallbackTables();
// //     } finally {
// //       setTablesLoading(false);
// //     }
// //   };

// //   const loadFallbackTables = () => {
// //     setTables([
// //       { value: 'ads_ad',          label: 'Ads (ads_ad)' },
// //       { value: 'ads_adimage',     label: 'Ad Images (ads_adimage)' },
// //       { value: 'ads_category',    label: 'Categories (ads_category)' },
// //       { value: 'ads_job',         label: 'Jobs (ads_job)' },
// //       { value: 'ads_shop',        label: 'Shops (ads_shop)' },
// //       { value: 'ads_shopimage',   label: 'Shop Images (ads_shopimage)' },
// //       { value: 'ads_userprofile', label: 'User Profiles (ads_userprofile)' },
// //       { value: 'auth_user',       label: 'Users (auth_user)' },
// //     ]);
// //   };

// //   // ✅ FIXED: Delay initial fetch by 800ms to let AuthContext restore token first
// //   useEffect(() => {
// //     if (hasFetched.current) return;
// //     hasFetched.current = true;

// //     const timer = setTimeout(() => {
// //       fetchAvailableTables();
// //     }, 800);

// //     return () => clearTimeout(timer);
// //   }, []);

// //   const currentNotebook = notebooks.find(n => n.id === currentNotebookId);

// //   useEffect(() => {
// //     if (currentNotebook?.cells?.length > 0) {
// //       setCells(currentNotebook.cells);
// //     } else {
// //       setCells([{ id: Date.now(), type: 'code', content: '', output: null, executing: false }]);
// //     }
// //   }, [currentNotebookId]);

// //   useEffect(() => {
// //     const t = setTimeout(() => {
// //       setNotebooks(prev => prev.map(nb =>
// //         nb.id === currentNotebookId ? { ...nb, cells, lastSaved: new Date() } : nb
// //       ));
// //     }, 1000);
// //     return () => clearTimeout(t);
// //   }, [cells, currentNotebookId]);

// //   useEffect(() => {
// //     try { localStorage.setItem('analyticsNotebooks', JSON.stringify(notebooks)); }
// //     catch (e) { console.error(e); }
// //   }, [notebooks]);

// //   useEffect(() => {
// //     try { localStorage.setItem('currentNotebookId', currentNotebookId.toString()); }
// //     catch (e) { console.error(e); }
// //   }, [currentNotebookId]);

// //   const createNotebook = () => {
// //     const nb = { id: Date.now(), name: `Notebook ${notebooks.length + 1}`, lastSaved: new Date(), cells: [] };
// //     setNotebooks([...notebooks, nb]);
// //     setCurrentNotebookId(nb.id);
// //     setShowNotebookDropdown(false);
// //   };

// //   const renameNotebook = () => {
// //     if (newNotebookName.trim()) {
// //       setNotebooks(prev => prev.map(nb => nb.id === currentNotebookId ? { ...nb, name: newNotebookName.trim() } : nb));
// //       setShowRenameModal(false);
// //       setNewNotebookName('');
// //     }
// //   };

// //   const deleteNotebook = (id) => {
// //     if (notebooks.length === 1) { alert('Cannot delete the last notebook'); return; }
// //     if (window.confirm('Delete this notebook?')) {
// //       setNotebooks(prev => prev.filter(nb => nb.id !== id));
// //       if (currentNotebookId === id) setCurrentNotebookId(notebooks.find(nb => nb.id !== id).id);
// //     }
// //   };

// //   const formatLastSaved = (date) => {
// //     if (!date) return '';
// //     const diff = Math.floor((Date.now() - new Date(date)) / 1000);
// //     if (diff < 60) return 'Just now';
// //     if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
// //     if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
// //     return new Date(date).toLocaleDateString();
// //   };

// //   const addCell = () => setCells([...cells, { id: Date.now(), type: 'code', content: '', output: null, executing: false }]);
// //   const updateCell = (id, content) => setCells(cells.map(c => c.id === id ? { ...c, content } : c));
// //   const deleteCell = (id) => { if (cells.length > 1) setCells(cells.filter(c => c.id !== id)); };
// //   const moveCellUp = (id) => {
// //     const i = cells.findIndex(c => c.id === id);
// //     if (i > 0) { const n = [...cells]; [n[i-1], n[i]] = [n[i], n[i-1]]; setCells(n); }
// //   };
// //   const moveCellDown = (id) => {
// //     const i = cells.findIndex(c => c.id === id);
// //     if (i < cells.length - 1) { const n = [...cells]; [n[i], n[i+1]] = [n[i+1], n[i]]; setCells(n); }
// //   };

// //   const executeCell = async (id) => {
// //     const cell = cells.find(c => c.id === id);
// //     if (!cell || cell.executing || !cell.content.trim()) return;

// //     setCells(prev => prev.map(c => c.id === id ? { ...c, executing: true, output: null } : c));

// //     try {
// //       const code = cell.content.trim();
// //       const lower = code.toLowerCase();
// //       let result;

// //       if (lower === 'show tables' || lower === 'list tables') {
// //         result = { type: 'table', data: tables.map(t => ({ 'Table': t.value, 'Description': t.label })), rowCount: tables.length };
// //       } else if (lower.startsWith('describe') || lower.startsWith('show columns')) {
// //         const tableName = code.split(/\s+/)[1];
// //         result = await callDescribe(tableName);
// //       } else if (lower.startsWith('select') || lower.startsWith('with') || lower.includes(' from ') ||
// //                  lower.startsWith('insert') || lower.startsWith('update') || lower.startsWith('delete')) {
// //         result = await callExecute(code);
// //       } else {
// //         result = { type: 'error', message: `Unknown command: "${code.split('\n')[0]}"\nTry: SELECT, SHOW TABLES, DESCRIBE <table>` };
// //       }

// //       setCells(prev => prev.map(c => c.id === id ? { ...c, executing: false, output: result } : c));
// //     } catch (err) {
// //       setCells(prev => prev.map(c => c.id === id ? { ...c, executing: false, output: { type: 'error', message: err.message } } : c));
// //     }
// //   };

// //   const callExecute = async (query) => {
// //     try {
// //       const res = await fetch('https://imselling-marketplace-production.up.railway.app/api/notebook/execute/', {
// //         method: 'POST',
// //         headers: getAuthHeaders(),
// //         body: JSON.stringify({ query, table: selectedTable }),
// //       });
// //       const data = await res.json();
// //       if (!res.ok) return { type: 'error', message: data.error || 'Query failed' };
// //       if (data.results !== undefined) {
// //         return { type: 'table', data: data.results, rowCount: data.count, executionTime: data.execution_time };
// //       }
// //       return { type: 'success', message: `✓ ${data.rows_affected ?? 0} row(s) affected (${data.execution_time}ms)` };
// //     } catch (err) {
// //       return { type: 'error', message: err.message };
// //     }
// //   };

// //   const callDescribe = async (tableName) => {
// //     try {
// //       const res = await fetch(`https://imselling-marketplace-production.up.railway.app/api/notebook/describe/${tableName}/`, { headers: getAuthHeaders() });
// //       const data = await res.json();
// //       if (!res.ok) return { type: 'error', message: data.error || 'Failed to describe table' };
// //       return { type: 'table', data: data.columns };
// //     } catch (err) {
// //       return { type: 'error', message: err.message };
// //     }
// //   };

// //   const runAllCells = () => cells.forEach(c => { if (c.type === 'code') executeCell(c.id); });
// //   const clearAllOutputs = () => setCells(cells.map(c => ({ ...c, output: null })));
// //   const handleKeyDown = (e, id) => { if (e.shiftKey && e.key === 'Enter') { e.preventDefault(); executeCell(id); } };

// //   const renderOutput = (output) => {
// //     if (!output) return null;
// //     switch (output.type) {
// //       case 'table':
// //         return (
// //           <div className="output-table-container">
// //             {output.rowCount !== undefined && (
// //               <div className="output-meta">{output.rowCount} rows{output.executionTime ? ` · ${output.executionTime}ms` : ''}</div>
// //             )}
// //             {output.data?.length > 0 ? (
// //               <div className="output-table-wrapper">
// //                 <table className="output-table">
// //                   <thead><tr>{Object.keys(output.data[0]).map((k, i) => <th key={i}>{k}</th>)}</tr></thead>
// //                   <tbody>{output.data.map((row, ri) => (
// //                     <tr key={ri}>{Object.values(row).map((v, ci) => (
// //                       <td key={ci}>{v === null ? <span className="null-value">NULL</span> : String(v)}</td>
// //                     ))}</tr>
// //                   ))}</tbody>
// //                 </table>
// //               </div>
// //             ) : <div className="empty-result">No rows returned</div>}
// //           </div>
// //         );
// //       case 'success':
// //         return <div className="output-success">✓ {output.message}</div>;
// //       case 'error':
// //         return <div className="output-error"><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>❌ {output.message}</pre></div>;
// //       case 'text':
// //         return <div className="output-text">{output.content}</div>;
// //       default:
// //         return <div className="output-text">{JSON.stringify(output)}</div>;
// //     }
// //   };

// //   const statusLabel = connectionStatus === 'connected' ? 'Connected'
// //     : connectionStatus === 'connecting' ? 'Connecting...'
// //     : 'Disconnected';

// //   return (
// //     <div className="analytics-notebook">
// //       {/* Header */}
// //       <div className="notebook-header">
// //         <div className="header-left">
// //           <div className="notebook-selector">
// //             <button className="current-notebook-btn" onClick={() => setShowNotebookDropdown(!showNotebookDropdown)}>
// //               <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
// //                 <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
// //                 <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
// //               </svg>
// //               <div className="notebook-info">
// //                 <span className="notebook-name">{currentNotebook?.name}</span>
// //                 <span className="notebook-saved">Saved {formatLastSaved(currentNotebook?.lastSaved)}</span>
// //               </div>
// //               <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
// //                 <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/>
// //               </svg>
// //             </button>

// //             {showNotebookDropdown && (
// //               <div className="notebook-dropdown">
// //                 <div className="notebook-dropdown-header">
// //                   <span>Notebooks</span>
// //                   <button onClick={createNotebook} className="create-notebook-btn">
// //                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
// //                       <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
// //                       <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
// //                     </svg>
// //                     New
// //                   </button>
// //                 </div>
// //                 {notebooks.map(nb => (
// //                   <div key={nb.id} className={`notebook-option ${currentNotebookId === nb.id ? 'active' : ''}`}>
// //                     <div className="notebook-option-content" onClick={() => { setCurrentNotebookId(nb.id); setShowNotebookDropdown(false); }}>
// //                       <span className="notebook-option-name">{nb.name}</span>
// //                       <span className="notebook-option-time">{formatLastSaved(nb.lastSaved)}</span>
// //                     </div>
// //                     <div className="notebook-option-actions">
// //                       <button onClick={e => { e.stopPropagation(); setNewNotebookName(nb.name); setCurrentNotebookId(nb.id); setShowRenameModal(true); setShowNotebookDropdown(false); }}>
// //                         <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// //                           <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
// //                           <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
// //                         </svg>
// //                       </button>
// //                       {notebooks.length > 1 && (
// //                         <button onClick={e => { e.stopPropagation(); deleteNotebook(nb.id); }}>
// //                           <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// //                             <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
// //                             <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
// //                           </svg>
// //                         </button>
// //                       )}
// //                     </div>
// //                   </div>
// //                 ))}
// //               </div>
// //             )}
// //           </div>
// //         </div>
// //         <div className="header-right">
// //           <button className={`toolbar-btn ${showQuickRef ? 'active' : ''}`} onClick={() => setShowQuickRef(!showQuickRef)} title="Quick Reference">
// //             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //               <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
// //               <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2"/>
// //               <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2"/>
// //             </svg>
// //           </button>
// //         </div>
// //       </div>

// //       {/* Toolbar */}
// //       <div className="notebook-toolbar">
// //         <div className="toolbar-left">
// //           <div className="connection-status">
// //             <div className={`status-indicator ${connectionStatus}`}></div>
// //             <span className="status-text">{statusLabel}</span>

// //             <div className="table-selector" onClick={() => setShowTableDropdown(!showTableDropdown)}>
// //               <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
// //                 <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
// //                 <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
// //                 <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
// //                 <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
// //               </svg>
// //               <span>{tablesLoading ? 'Loading...' : (tables.find(t => t.value === selectedTable)?.label || 'Select Table')}</span>
// //               <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// //                 <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/>
// //               </svg>

// //               {showTableDropdown && (
// //                 <div className="table-dropdown">
// //                   <div
// //                     className="table-option"
// //                     style={{ color: '#6366f1', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}
// //                     onClick={e => { e.stopPropagation(); fetchAvailableTables(0); setShowTableDropdown(false); }}
// //                   >
// //                     🔄 Refresh Tables ({tables.length} loaded)
// //                   </div>
// //                   {tables.map(t => (
// //                     <div key={t.value} className={`table-option ${selectedTable === t.value ? 'active' : ''}`}
// //                       onClick={e => { e.stopPropagation(); setSelectedTable(t.value); setShowTableDropdown(false); }}>
// //                       {t.label}
// //                     </div>
// //                   ))}
// //                 </div>
// //               )}
// //             </div>
// //           </div>

// //           <div className="toolbar-divider"></div>
// //           <button className="toolbar-btn" onClick={addCell} title="Add Cell">
// //             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //               <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
// //               <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
// //             </svg>
// //           </button>
// //           <button className="toolbar-btn" onClick={runAllCells} title="Run All">
// //             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //               <polygon points="5 3 19 12 5 21 5 3" stroke="currentColor" strokeWidth="2"/>
// //             </svg>
// //           </button>
// //           <button className="toolbar-btn" onClick={clearAllOutputs} title="Clear Outputs">
// //             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //               <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
// //               <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" strokeWidth="2"/>
// //             </svg>
// //           </button>
// //         </div>
// //         <div className="toolbar-right">
// //           <button className="toolbar-btn-primary" onClick={addCell}>
// //             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //               <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
// //               <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
// //             </svg>
// //             Code
// //           </button>
// //         </div>
// //       </div>

// //       {/* Cells */}
// //       <div className="cells-container">
// //         {cells.map((cell, index) => (
// //           <div key={cell.id} className={`notebook-cell ${cell.executing ? 'executing' : ''}`}>
// //             <div className="cell-sidebar">
// //               <div className="cell-number">[{index + 1}]:</div>
// //             </div>
// //             <div className="cell-content">
// //               <div className="cell-input">
// //                 <textarea
// //                   className="cell-textarea"
// //                   value={cell.content}
// //                   onChange={e => updateCell(cell.id, e.target.value)}
// //                   onKeyDown={e => handleKeyDown(e, cell.id)}
// //                   placeholder={`# Enter SQL query\nSELECT * FROM ${selectedTable} LIMIT 10;\n\n# Or try:\nSHOW TABLES\nDESCRIBE ${selectedTable}`}
// //                   spellCheck={false}
// //                 />
// //                 <div className="cell-actions">
// //                   <button className="cell-action-btn execute-btn" onClick={() => executeCell(cell.id)} disabled={cell.executing} title="Run (Shift+Enter)">
// //                     {cell.executing ? <div className="spinner-small"></div> : (
// //                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/></svg>
// //                     )}
// //                   </button>
// //                   <button className="cell-action-btn" onClick={() => moveCellUp(cell.id)} title="Move Up">
// //                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="18 15 12 9 6 15" stroke="currentColor" strokeWidth="2"/></svg>
// //                   </button>
// //                   <button className="cell-action-btn" onClick={() => moveCellDown(cell.id)} title="Move Down">
// //                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2"/></svg>
// //                   </button>
// //                   <button className="cell-action-btn delete-btn" onClick={() => deleteCell(cell.id)} title="Delete">
// //                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
// //                       <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
// //                       <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
// //                     </svg>
// //                   </button>
// //                 </div>
// //               </div>
// //               {cell.output && <div className="cell-output">{renderOutput(cell.output)}</div>}
// //             </div>
// //           </div>
// //         ))}
// //         <div className="add-cell-section">
// //           <button className="add-cell-btn" onClick={addCell}>
// //             <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //               <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
// //               <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
// //             </svg>
// //             Add Cell
// //           </button>
// //         </div>
// //       </div>

// //       {/* Quick Reference */}
// //       {showQuickRef && (
// //         <div className="quick-reference">
// //           <div className="quick-ref-header">
// //             <h3>Quick Reference</h3>
// //             <button onClick={() => setShowQuickRef(false)} className="close-quick-ref">
// //               <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
// //                 <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
// //                 <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
// //               </svg>
// //             </button>
// //           </div>
// //           <div className="reference-section">
// //             <h4>Available Tables ({tables.length})</h4>
// //             {tables.map(t => <code key={t.value} style={{ display: 'block', marginBottom: 4 }}>{t.value}</code>)}
// //           </div>
// //           <div className="reference-section">
// //             <h4>Examples</h4>
// //             <code>SELECT * FROM ads_ad LIMIT 10;</code>
// //             <code>SELECT * FROM ads_shop LIMIT 10;</code>
// //             <code>SELECT * FROM ads_job LIMIT 10;</code>
// //             <code>SHOW TABLES</code>
// //             <code>DESCRIBE ads_ad</code>
// //           </div>
// //           <div className="reference-section">
// //             <h4>Shortcuts</h4>
// //             <div className="shortcut"><kbd>Shift</kbd> + <kbd>Enter</kbd> — Run Cell</div>
// //           </div>
// //         </div>
// //       )}

// //       {/* Rename Modal */}
// //       {showRenameModal && (
// //         <div className="modal-overlay" onClick={() => setShowRenameModal(false)}>
// //           <div className="modal-box-small" onClick={e => e.stopPropagation()}>
// //             <h3>Rename Notebook</h3>
// //             <input type="text" className="rename-input" value={newNotebookName}
// //               onChange={e => setNewNotebookName(e.target.value)}
// //               onKeyDown={e => e.key === 'Enter' && renameNotebook()}
// //               autoFocus placeholder="Enter notebook name" />
// //             <div className="modal-actions-small">
// //               <button className="btn-cancel-small" onClick={() => setShowRenameModal(false)}>Cancel</button>
// //               <button className="btn-submit-small" onClick={renameNotebook}>Rename</button>
// //             </div>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }

// // export default AnalyticsView;